import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Booking requests (patients asking for an appointment with a provider
 * who cannot be booked online yet).
 *
 * Spec: `_docs/booking-requests-unclaimed-providers.md` §7, contract §4.4 +
 * §6.4 (`AdminBookingRequest`).
 *
 * Endpoints (apps/api, superadmin chain like `adminClaims`):
 *   GET  /api/admin/booking-requests?status=&country=&recipientKind=&limit=&offset=
 *   POST /api/admin/booking-requests/:id/resend   → re-runs delivery
 *
 * Why this screen matters: at launch most providers are scraped and carry no
 * email/phone, so `recipientKind: "none"` ("unreachable") is a first-class
 * path — the request is stored and ops add a contact on the provider, then
 * press Resend here.
 *
 * Data via `useQuery` with `dbTarget` in the key so the admin Local↔Prod DB
 * toggle refetches (`reference_admin_db_toggle_fetch_rule`).
 */

type DeliveryKind = "user" | "provider" | "facility" | "none";
type DeliveryVia = "in_app" | "email" | "sms" | "none";
type ClosedReason = "provider_opted_out" | "provider_removed";

/**
 * `notifyError` value for a request that joined an earlier message to the
 * same doctor/clinic instead of sending a new one — not a delivery failure.
 */
const NOTIFY_THROTTLED = "throttled";

interface AdminBookingRequest {
  id: string;
  status: string;
  countryCode: string;
  consultationType: string;
  preferredDates: string[];
  preferredTimeOfDay: string;
  urgency: string;
  recipientKind: DeliveryKind | null;
  notifiedVia: DeliveryVia | null;
  notifiedTo: string | null;
  notifiedAt: string | null;
  notifyError: string | null;
  reminderSentAt: string | null;
  expiresAt: string;
  createdAt: string;
  respondedAt: string | null;
  appointmentId: string | null;
  /** Set when `status === "closed"`; null otherwise. */
  closedReason: ClosedReason | null;
  provider: {
    id: string;
    firstName: string;
    lastName: string;
    primaryCity: string | null;
    isClaimed: boolean;
    contactOptOut: boolean;
  };
  patient: { firstName: string; lastName: string };
  facility: { id: string; name: string | Record<string, string> } | null;
}

interface ListResponse {
  requests: AdminBookingRequest[];
  total: number;
  counts: {
    byStatus: Record<string, number>;
    byRecipientKind: Record<string, number>;
  };
}

const STATUS_FILTERS = [
  "all",
  "pending",
  "accepted",
  "declined",
  "expired",
  "cancelled",
  "closed",
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const RECIPIENT_FILTERS = ["all", "user", "provider", "facility", "none"] as const;
type RecipientFilter = (typeof RECIPIENT_FILTERS)[number];

const PAGE_SIZE = 50;
const SELECT_CLASS =
  "border-border bg-card focus:ring-primary/30 focus:border-primary h-11 rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-inset";

type BadgeVariant = "warning" | "success" | "destructive" | "neutral" | "info" | "outline";

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  pending: "warning",
  accepted: "success",
  declined: "destructive",
  expired: "neutral",
  cancelled: "neutral",
  closed: "neutral",
};

const RECIPIENT_VARIANT: Record<string, BadgeVariant> = {
  user: "success",
  provider: "info",
  facility: "info",
  none: "destructive",
};

interface BookingRequestsSearch {
  status?: StatusFilter;
  country?: string;
  recipientKind?: RecipientFilter;
  offset?: number;
}

function parseOffset(value: unknown): number | undefined {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.floor(n);
}

export const Route = createFileRoute("/_authenticated/booking-requests")({
  validateSearch: (search: Record<string, unknown>): BookingRequestsSearch => ({
    status: STATUS_FILTERS.includes(search.status as StatusFilter)
      ? (search.status as StatusFilter)
      : undefined,
    country:
      typeof search.country === "string" && /^[A-Za-z]{2}$/.test(search.country)
        ? search.country.toUpperCase()
        : undefined,
    recipientKind: RECIPIENT_FILTERS.includes(search.recipientKind as RecipientFilter)
      ? (search.recipientKind as RecipientFilter)
      : undefined,
    offset: parseOffset(search.offset),
  }),
  component: BookingRequestsPage,
});

/** `YYYY-MM-DD` → locale short date, parsed as a LOCAL date (no UTC shift). */
function formatPreferredDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function facilityName(
  facility: { name: string | Record<string, string> } | null,
  locale: string,
): string | null {
  if (!facility) return null;
  if (typeof facility.name === "string") return facility.name;
  return facility.name[locale] ?? facility.name.en ?? facility.name.ro ?? null;
}

function BookingRequestsPage() {
  const { t, i18n } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { dbTarget } = useAdminContext();
  const { visibleCountries: countries } = useAdminCountries();
  const queryClient = useQueryClient();

  const [resendingId, setResendingId] = useState<string | null>(null);

  const status: StatusFilter = search.status ?? "pending";
  const recipientKind: RecipientFilter = search.recipientKind ?? "all";
  const country = search.country ?? "";
  const offset = search.offset ?? 0;

  /** Filter changes reset paging; only an explicit `offset` patch keeps it. */
  const setSearch = (patch: Partial<BookingRequestsSearch>) => {
    void navigate({
      search: (prev) => ({ ...prev, offset: undefined, ...patch }),
      replace: true,
    });
  };

  const listQuery = useQuery({
    queryKey: [
      "admin",
      "booking-requests",
      "list",
      status,
      country,
      recipientKind,
      offset,
      dbTarget,
    ],
    queryFn: async (): Promise<ListResponse> => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (status !== "all") params.set("status", status);
      if (country) params.set("country", country);
      if (recipientKind !== "all") params.set("recipientKind", recipientKind);
      const data = await apiFetch.get<ListResponse>(
        `/api/admin/booking-requests?${params.toString()}`,
      );
      return {
        requests: data.requests ?? [],
        total: data.total ?? 0,
        counts: {
          byStatus: data.counts?.byStatus ?? {},
          byRecipientKind: data.counts?.byRecipientKind ?? {},
        },
      };
    },
    staleTime: 30_000,
  });

  const requests = listQuery.data?.requests ?? [];
  const total = listQuery.data?.total ?? 0;
  const byStatus = listQuery.data?.counts.byStatus ?? {};
  const byRecipientKind = listQuery.data?.counts.byRecipientKind ?? {};

  const statusLabel = (value: string): string => {
    switch (value) {
      case "pending":
        return t`Pending`;
      case "accepted":
        return t`Accepted`;
      case "declined":
        return t`Declined`;
      case "expired":
        return t`Expired`;
      case "cancelled":
        return t`Cancelled`;
      case "closed":
        return t`Closed`;
      case "all":
        return t`All statuses`;
      default:
        return value;
    }
  };

  const recipientLabel = (value: string): string => {
    switch (value) {
      case "user":
        return t`In app`;
      case "provider":
        return t`Doctor`;
      case "facility":
        return t`Clinic`;
      case "none":
        return t`Unreachable`;
      case "all":
        return t`All recipients`;
      default:
        return value;
    }
  };

  const viaLabel = (value: string | null): string => {
    switch (value) {
      case "in_app":
        return t`in app`;
      case "email":
        return t`email`;
      case "sms":
        return t`SMS`;
      case "none":
        return t`not delivered`;
      default:
        return "—";
    }
  };

  const closedReasonLabel = (value: ClosedReason | null | undefined): string => {
    switch (value) {
      case "provider_opted_out":
        return t`Doctor opted out of booking requests`;
      case "provider_removed":
        return t`Doctor profile removed`;
      default:
        return "—";
    }
  };

  /** Batched requests carry `notifyError: "throttled"` — shown as a delivery, not an error. */
  const deliveryLabel = (r: AdminBookingRequest): string =>
    r.notifyError === NOTIFY_THROTTLED
      ? t`Batched with an earlier message`
      : viaLabel(r.notifiedVia);

  const deliveryError = (r: AdminBookingRequest): string | null =>
    r.notifyError && r.notifyError !== NOTIFY_THROTTLED ? r.notifyError : null;

  const typeLabel = (value: string): string =>
    value === "video" ? t`Video` : value === "in_office" ? t`In office` : value;

  const timeOfDayLabel = (value: string): string => {
    switch (value) {
      case "morning":
        return t`morning`;
      case "afternoon":
        return t`afternoon`;
      case "evening":
        return t`evening`;
      default:
        return "";
    }
  };

  const preferredSummary = (r: AdminBookingRequest): string => {
    // `preferred_dates` is a JSONB column — guard the shape rather than trust
    // it, so one odd row cannot blank the whole queue.
    const dates = Array.isArray(r.preferredDates) ? r.preferredDates : [];
    const base =
      dates.length > 0 ? dates.map(formatPreferredDate).join(", ") : t`As soon as possible`;
    const tod = timeOfDayLabel(r.preferredTimeOfDay);
    return tod ? `${base} · ${tod}` : base;
  };

  const handleResend = async (id: string) => {
    setResendingId(id);
    const toast = showToast(t`Resending the request…`, "loading");
    try {
      // `{}` rather than no body: the endpoint takes no input, but a Hono
      // handler that calls `c.req.json()` would throw on a bodyless POST.
      const data = await apiFetch.post<{ delivery?: { kind?: string; via?: string | null } }>(
        `/api/admin/booking-requests/${id}/resend`,
        {},
      );
      const via = data?.delivery?.via ?? null;
      if (via && via !== "none") {
        toast.update(t`Request resent (${viaLabel(via)})`, "success");
      } else {
        toast.update(t`Still no way to reach this doctor — add a contact first`, "error");
      }
      await queryClient.invalidateQueries({ queryKey: ["admin", "booking-requests"] });
    } catch (error) {
      const code = error instanceof ApiError ? String(error.status) : t`unknown error`;
      toast.update(t`Resend failed (${code})`, "error");
    } finally {
      setResendingId(null);
    }
  };

  const stats: {
    key: string;
    label: string;
    value: number;
    tone: string;
    onClick: () => void;
    active: boolean;
  }[] = [
    {
      key: "pending",
      label: t`Pending`,
      value: byStatus.pending ?? 0,
      tone: "text-warning-strong",
      onClick: () => setSearch({ status: "pending", recipientKind: undefined }),
      active: status === "pending" && recipientKind === "all",
    },
    {
      key: "unreachable",
      label: t`Unreachable`,
      value: byRecipientKind.none ?? 0,
      tone: "text-destructive",
      // `status: "all"` so the chip lands on exactly what its count means:
      // every unreachable request, not just the pending ones.
      onClick: () => setSearch({ recipientKind: "none", status: "all" }),
      active: recipientKind === "none",
    },
    {
      key: "accepted",
      label: t`Accepted`,
      value: byStatus.accepted ?? 0,
      tone: "text-success-strong",
      onClick: () => setSearch({ status: "accepted", recipientKind: undefined }),
      active: status === "accepted" && recipientKind === "all",
    },
    {
      key: "expired",
      label: t`Expired`,
      value: byStatus.expired ?? 0,
      tone: "text-muted-foreground",
      onClick: () => setSearch({ status: "expired", recipientKind: undefined }),
      active: status === "expired" && recipientKind === "all",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Booking requests</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Requests patients sent to doctors who cannot be booked online yet — and how we delivered
            them.
          </Trans>
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <button
            key={stat.key}
            type="button"
            onClick={stat.onClick}
            aria-pressed={stat.active}
            className="focus-visible:ring-ring/50 w-full rounded-lg text-left focus-visible:ring-3 focus-visible:outline-none"
          >
            <Card
              className={
                stat.active ? "ring-primary ring-2" : "hover:ring-primary/40 transition-all"
              }
            >
              <CardContent className="p-4">
                <p className="text-muted-foreground text-xs font-medium uppercase">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.tone}`}>{stat.value}</p>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="sr-only" htmlFor="br-status">
          <Trans>Status</Trans>
        </label>
        <select
          id="br-status"
          className={SELECT_CLASS}
          value={status}
          onChange={(e) => setSearch({ status: e.target.value as StatusFilter })}
        >
          {STATUS_FILTERS.map((value) => (
            <option key={value} value={value}>
              {statusLabel(value)}
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="br-country">
          <Trans>Country</Trans>
        </label>
        <select
          id="br-country"
          className={SELECT_CLASS}
          value={country}
          onChange={(e) => setSearch({ country: e.target.value || undefined })}
        >
          <option value="">{t`All countries`}</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name[i18n.locale] ?? c.name.en ?? c.code}
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="br-recipient">
          <Trans>Delivered to</Trans>
        </label>
        <select
          id="br-recipient"
          className={SELECT_CLASS}
          value={recipientKind}
          onChange={(e) => setSearch({ recipientKind: e.target.value as RecipientFilter })}
        >
          {RECIPIENT_FILTERS.map((value) => (
            <option key={value} value={value}>
              {recipientLabel(value)}
            </option>
          ))}
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQuery.isError ? (
            <div className="text-muted-foreground space-y-3 p-8 text-center text-sm">
              <p>
                {listQuery.error instanceof ApiError ? (
                  <Trans>Could not load booking requests ({listQuery.error.status}).</Trans>
                ) : (
                  <Trans>Could not load booking requests.</Trans>
                )}
              </p>
              <Button
                variant="outline"
                className="h-11 px-4"
                onClick={() => void listQuery.refetch()}
              >
                <Trans>Try again</Trans>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Trans>Provider</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Patient</Trans>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      <Trans>Country</Trans>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      <Trans>Type</Trans>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      <Trans>Preferred</Trans>
                    </TableHead>
                    <TableHead className="hidden sm:table-cell">
                      <Trans>Delivery</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Status</Trans>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      <Trans>Created</Trans>
                    </TableHead>
                    <TableHead className="text-right">
                      <Trans>Actions</Trans>
                    </TableHead>
                    <TableHead className="w-8 md:hidden" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listQuery.isLoading
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <TableRow key={`skeleton-${i}`}>
                          <TableCell>
                            <Skeleton className="h-4 w-32" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-24" />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Skeleton className="h-4 w-8" />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Skeleton className="h-4 w-16" />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Skeleton className="h-4 w-28" />
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Skeleton className="h-4 w-28" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-16 rounded-full" />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Skeleton className="h-4 w-20" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="ml-auto h-8 w-20" />
                          </TableCell>
                          <TableCell className="w-8 md:hidden" />
                        </TableRow>
                      ))
                    : requests.map((r) => {
                        const clinic = facilityName(r.facility, i18n.locale);
                        return (
                          <ExpandableRow
                            key={r.id}
                            mobileDetails={[
                              { label: <Trans>Country</Trans>, value: r.countryCode },
                              {
                                label: <Trans>Type</Trans>,
                                value: typeLabel(r.consultationType),
                              },
                              { label: <Trans>Preferred</Trans>, value: preferredSummary(r) },
                              {
                                label: <Trans>Delivery</Trans>,
                                value: (
                                  <div className="space-y-0.5">
                                    <p>
                                      {recipientLabel(r.recipientKind ?? "none")} ·{" "}
                                      {deliveryLabel(r)}
                                    </p>
                                    {r.notifiedTo && (
                                      <p className="text-muted-foreground text-xs break-all">
                                        {r.notifiedTo}
                                      </p>
                                    )}
                                    {deliveryError(r) && (
                                      <p className="text-destructive text-xs break-all">
                                        {deliveryError(r)}
                                      </p>
                                    )}
                                  </div>
                                ),
                              },
                              ...(r.status === "closed"
                                ? [
                                    {
                                      label: <Trans>Closed because</Trans>,
                                      value: closedReasonLabel(r.closedReason),
                                    },
                                  ]
                                : []),
                              {
                                label: <Trans>Urgency</Trans>,
                                value: r.urgency === "urgent" ? t`Urgent` : t`Routine`,
                              },
                              { label: <Trans>Clinic</Trans>, value: clinic ?? "—" },
                              {
                                label: <Trans>Created</Trans>,
                                value: formatDateTime(r.createdAt),
                              },
                              {
                                label: <Trans>Expires</Trans>,
                                value: formatDateTime(r.expiresAt),
                              },
                              {
                                label: <Trans>Reminder sent</Trans>,
                                value: formatDateTime(r.reminderSentAt),
                              },
                            ]}
                          >
                            <TableCell>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium">
                                  {r.provider.firstName} {r.provider.lastName}
                                </p>
                                <div className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs">
                                  <span className="truncate">{r.provider.primaryCity ?? "—"}</span>
                                  {!r.provider.isClaimed && (
                                    <Badge variant="neutral" className="text-[0.625rem]">
                                      <Trans>Unclaimed</Trans>
                                    </Badge>
                                  )}
                                  {r.provider.contactOptOut && (
                                    <Badge variant="destructive" className="text-[0.625rem]">
                                      <Trans>Opted out</Trans>
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {r.patient.firstName} {r.patient.lastName}
                              </span>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <span className="text-xs">{r.countryCode}</span>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-xs">{typeLabel(r.consultationType)}</span>
                                {r.urgency === "urgent" && (
                                  <Badge variant="warning" className="text-[0.625rem]">
                                    <Trans>Urgent</Trans>
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <span className="text-muted-foreground text-xs">
                                {preferredSummary(r)}
                              </span>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <div className="space-y-1">
                                <Badge
                                  variant={
                                    RECIPIENT_VARIANT[r.recipientKind ?? "none"] ?? "outline"
                                  }
                                  className="text-[0.625rem]"
                                >
                                  {recipientLabel(r.recipientKind ?? "none")}
                                </Badge>
                                <p className="text-muted-foreground text-xs">
                                  {deliveryLabel(r)}
                                  {r.notifiedTo ? ` · ${r.notifiedTo}` : ""}
                                </p>
                                {deliveryError(r) && (
                                  <p className="text-destructive text-xs break-all">
                                    {deliveryError(r)}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={STATUS_VARIANT[r.status] ?? "outline"}>
                                {statusLabel(r.status)}
                              </Badge>
                              {r.status === "closed" && (
                                <p className="text-muted-foreground mt-1 hidden max-w-[12rem] text-xs md:block">
                                  {closedReasonLabel(r.closedReason)}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <span className="text-muted-foreground text-xs">
                                {formatDateTime(r.createdAt)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {r.status === "pending" && (
                                <Button
                                  variant="outline"
                                  className="h-11 px-4"
                                  disabled={resendingId === r.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleResend(r.id);
                                  }}
                                >
                                  <Trans>Resend</Trans>
                                </Button>
                              )}
                            </TableCell>
                          </ExpandableRow>
                        );
                      })}
                </TableBody>
              </Table>

              {!listQuery.isLoading && requests.length === 0 && (
                <div className="text-muted-foreground p-8 text-center text-sm">
                  <Trans>No booking requests match these filters.</Trans>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Pagination
        offset={offset}
        limit={PAGE_SIZE}
        total={total}
        onChange={(next) => setSearch({ offset: next > 0 ? next : undefined })}
      />
    </div>
  );
}
